import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useRef } from 'react'

import { isApiError } from '@/api/client'
import { keys } from '@/api/keys'
import { deleteStories, deleteStory, type StoryListResponse, type StoryRow } from '@/api/stories'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'

/** What the dialog needs to name a story; `status` only adds the "still being written" line. */
export type StoryRef = Pick<StoryRow, 'uid' | 'title'> & { status?: StoryRow['status'] }

export const DELETE_STORIES_MUTATION = ['stories', 'delete'] as const

const quoted = (title: string) => `“${title}”`
const MAX_LISTED = 5

/**
 * Delete one or many stories. The list drops them at once (every cached
 * `keys.stories.list()` page), comes back if the server refuses, and is
 * re-fetched either way. One story goes through `DELETE /api/stories/{uid}`
 * (a 404 there means it is already gone, which is what was asked for);
 * several go through `POST /api/stories/delete` in one request.
 */
export function useDeleteStories() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DELETE_STORIES_MUTATION,
    mutationFn: async (targets: StoryRef[]): Promise<number> => {
      if (targets.length === 1) {
        try {
          await deleteStory(targets[0]!.uid)
          return 1
        } catch (err) {
          if (isApiError(err) && err.isNotFound) return 0
          throw err
        }
      }
      return (await deleteStories(targets.map((t) => t.uid))).deleted
    },
    onMutate: async (targets) => {
      const gone = new Set(targets.map((t) => t.uid))
      const listKey = keys.stories.list()
      await qc.cancelQueries({ queryKey: listKey })
      const before = qc.getQueriesData<StoryListResponse>({ queryKey: listKey })
      qc.setQueriesData<StoryListResponse>({ queryKey: listKey }, (old) => {
        if (!old) return old
        const items = old.items.filter((s) => !gone.has(s.uid))
        return { items, total: Math.max(0, old.total - (old.items.length - items.length)) }
      })
      return { before }
    },
    onError: (err, targets, context) => {
      for (const [key, data] of context?.before ?? []) qc.setQueryData(key, data)
      toast.error(targets.length === 1 ? `Could not delete ${quoted(targets[0]!.title)}` : `Could not delete ${targets.length} stories`, {
        description: err instanceof Error ? err.message : String(err),
      })
    },
    onSuccess: (deleted, targets) => {
      if (targets.length === 1) {
        const name = quoted(targets[0]!.title)
        if (deleted) toast.success(`Deleted ${name}`)
        else toast.info(`${name} was already deleted`)
      } else if (deleted === targets.length) {
        toast.success(`Deleted ${deleted} stories`)
      } else {
        toast.success(`Deleted ${deleted} of ${targets.length} stories`, { description: 'The others were already gone.' })
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.stories.list() }),
  })
}

export interface DeleteStoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** One story, or the selection for "Delete selected". */
  stories: StoryRef[]
  /** After the server confirmed; `deleted` is how many were actually removed, `stories` what was asked. */
  onDeleted?: (deleted: number, stories: StoryRef[]) => void
  /** Where focus goes when the dialog closes after a delete (the trigger may be gone with its card). */
  focusAfterDelete?: () => HTMLElement | null
}

/**
 * The destructive confirm: Cancel is focused on open, so Enter or Escape does
 * nothing irreversible; Delete is the red button and the only way through.
 * The dialog closes as soon as Delete is pressed — the list has already
 * dropped the story optimistically and a toast reports the outcome.
 */
export function DeleteStoryDialog({ open, onOpenChange, stories, onDeleted, focusAfterDelete }: DeleteStoryDialogProps) {
  const del = useDeleteStories()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const confirmed = useRef(false)
  // Radix returns focus to its own `DialogTrigger`; this dialog is opened from a plain
  // button (or several), so remember whatever had focus and hand it back ourselves.
  const returnTo = useRef<HTMLElement | null>(null)
  const one = stories.length === 1 ? stories[0]! : null
  const generating = stories.filter((s) => s.status === 'generating').length

  const confirm = () => {
    if (stories.length === 0 || confirmed.current) return
    confirmed.current = true
    del.mutate(stories, { onSuccess: (deleted, targets) => onDeleted?.(deleted, targets) })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        role="alertdialog"
        size="sm"
        hideClose
        onOpenAutoFocus={(e) => {
          // Fired before focus moves in, so `activeElement` is still the button that opened us.
          returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
          e.preventDefault()
          confirmed.current = false
          cancelRef.current?.focus()
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          const opener = returnTo.current?.isConnected ? returnTo.current : null
          const target = (confirmed.current ? focusAfterDelete?.() : null) ?? opener
          target?.focus()
        }}
      >
        <DialogHeader className="pr-0">
          <DialogTitle>{one ? `Delete ${quoted(one.title)}?` : `Delete ${stories.length} stories?`}</DialogTitle>
          <DialogDescription>
            {one ? 'This removes the story and its recording.' : 'This removes the stories and their recordings.'} This can't be undone.
          </DialogDescription>
        </DialogHeader>
        {one ? null : (
          <ul className="flex flex-col gap-1 text-sm text-ink-2" aria-label="Stories to delete">
            {stories.slice(0, MAX_LISTED).map((s) => (
              <li key={s.uid} className="truncate">
                {quoted(s.title)}
              </li>
            ))}
            {stories.length > MAX_LISTED ? <li>and {stories.length - MAX_LISTED} more</li> : null}
          </ul>
        )}
        {generating ? (
          <p className="rounded-xl bg-warn-soft px-3 py-2 text-sm text-ink">
            {generating === 1 && one ? 'It is still being written; that stops too.' : 'Stories still being written stop too.'}
          </p>
        ) : null}
        <DialogFooter>
          <Button ref={cancelRef} variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirm}>
            <Trash2 aria-hidden />
            {one ? 'Delete' : `Delete ${stories.length} stories`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
